'use client';

import { useState } from 'react';
import { faComment } from '@fortawesome/pro-light-svg-icons';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';
import {
  EntityRatingComment,
  EntityRatingCommentCount,
} from '@medprev-web-app/api-graphql-client';

import { EntityComment } from './entity-comment.component';

import { RatingComment } from '@/types/rating.type';
import { Button } from '@/components/ui/button/button.component';
import { useLoading } from '@/hooks/use-loading.hook';
import { fetchEntityCommentCountIds, getCommentsAttributes } from '@/app/(main)/(pages)/_services/get-comments-attributes.service';
import { AnalyticsEnumKeyType } from '@/types/enums/analytics/analytics.enum';
import { getLogger } from '@/logger/logger';

interface EntityLoadMoreCommentsProps {
  professionalId?: string;
  partnerFacilityId?: string;
  countComments?: EntityRatingCommentCount | null;
  initialComments?: RatingComment[];
  analyticsEventShowMore?: AnalyticsEnumKeyType;
  analyticsEventShowMoreComments?: AnalyticsEnumKeyType;
}

export function EntityLoadMoreComments({
  professionalId,
  partnerFacilityId,
  countComments,
  initialComments,
  analyticsEventShowMore,
  analyticsEventShowMoreComments,
}: EntityLoadMoreCommentsProps) {
  const [comments, setComments] = useState<EntityRatingComment[]>(
    initialComments!,
  );
  const [page, setPage] = useState(2);

  const { isLoading, startLoading, stopLoading } = useLoading();

  async function loadMoreComments() {
    startLoading();

    try {
      const entityIds = await fetchEntityCommentCountIds(
        professionalId,
        partnerFacilityId,
        page,
      );

      const comments = await getCommentsAttributes(
        entityIds as { id: string }[],
      );

      if (!comments?.length) {
        return;
      }

      const validComments = comments.filter(
        (comment) => comment !== null && comment !== undefined,
      );

      setComments((prev) => [...prev, ...validComments]);

      setPage((prev) => prev + 1);
    } catch (error) {
      const logger = getLogger('EntityLoadMoreComments');
      logger.warn('Error on load more comments', { page, professionalId, partnerFacilityId, countComments, initialComments, error });
    } finally {
      stopLoading();
    }
  }

  const showMoreComments
    = Number(countComments?.total) > (comments?.length || 0);

  return (
    <div className="mb-7 md:mb-10">
      {comments?.map((comment, index) => (
        <EntityComment
          comment={comment}
          key={comment.id}
          lastItem={comments.length - 1 === index}
          index={index}
          data-analytics-event={analyticsEventShowMore}
        />
      ))}
      {showMoreComments && (
        <>
          <Button
            variant="secondary"
            startIcon={faComment}
            className="mt-5"
            isLoading={isLoading}
            data-testid={DataTestIdEnum.ENTITY_SHOW_MORE_COMMENTS_BUTTON}
            data-analytics-event={analyticsEventShowMoreComments}
            onClick={loadMoreComments}
          >
            Mostrar mais comentários
          </Button>
        </>
      )}
    </div>
  );
}
